import React, { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import QRCode from 'react-qr-code';

/**
 * Dedicated, browser-independent QR label print page.
 *
 * Route: /print-label/:code?name=...&price=...
 *
 * Renders ONLY the label, at the same fixed physical size as the frame stock
 * stickers (40mm × 20mm) and with the same markup/class names, so client cards
 * and frame labels come off the same roll looking identical. Auto-prints on
 * load and closes the popup afterwards. The data is passed via the URL (no API
 * call / auth needed), so the popup renders instantly and consistently.
 */
export default function PrintLabel() {
  const { code = '' } = useParams();
  const [params] = useSearchParams();
  const name = params.get('name') || '';
  const price = params.get('price') || '';
  const qrValue = code || 'UNKNOWN';

  useEffect(() => {
    const onAfterPrint = () => { try { window.close(); } catch (e) { /* ignore */ } };
    window.addEventListener('afterprint', onAfterPrint);
    // Give the QR + layout one paint, then print. Works the same in Chrome/FF.
    const t = setTimeout(() => {
      window.focus();
      window.print();
    }, 400);
    return () => {
      clearTimeout(t);
      window.removeEventListener('afterprint', onAfterPrint);
    };
  }, []);

  return (
    <>
      <style>{`
        @page { size: 40mm 20mm; margin: 0; }

        * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }

        /* font-size/line-height 0 on the page root kills stray whitespace line
           boxes between elements, which can otherwise spill a few px onto a
           phantom 2nd (blank) label. Real text restores its own size via the
           .label-* classes. Body is 19mm (not 20) for the same reason. */
        html, body, #root {
          width: 40mm;
          height: 19mm;
          max-height: 19mm;
          margin: 0 !important;
          padding: 0 !important;
          overflow: hidden;
          background: #fff;
          font-size: 0;
          line-height: 0;
        }

        /* Outer container fills the label and centers content on both axes.
           Inner row holds QR (left) + text (right). No rotation, offsets or
           absolute positioning, so Chrome and Firefox agree. */
        .print-label {
          width: 40mm;
          height: 20mm;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          padding: 1.5mm;
          background: #fff;
          page-break-after: avoid;
          break-after: avoid;
          page-break-inside: avoid;
          break-inside: avoid;
        }
        .label-inner {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: center;
          gap: 1.5mm;
          width: 100%;
        }
        /* 13.5mm QR (vs the frame sticker's 15mm) buys the text column ~22mm,
           which is what a full client name plus the 12-character code needs.
           Still a comfortably scannable size for a short alphanumeric code. */
        .qr-section {
          width: 13.5mm;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .qr-section svg, .qr-section .qr svg, .qr {
          width: 12.5mm !important;
          height: 12.5mm !important;
          display: block;
          image-rendering: pixelated;
        }
        .text-section { flex: 1; min-width: 0; overflow: hidden; }
        /* Names are long and vary, so the title wraps to two lines instead of
           being cut off after one word. */
        .label-title {
          font-size: 10px;
          font-weight: 700;
          line-height: 1.15;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          word-break: break-word;
        }
        /* The code is the whole point of printing the label, so it must always
           render in full. Monospace makes the width deterministic (every glyph
           ~0.55em, so a 12-char code is ~6.6em ≈ 18mm inside a 22mm column) —
           a proportional font could overflow on a code of all-wide letters.
           It also reads better when someone is typing the code in by hand. */
        .label-code {
          font-family: Consolas, Menlo, "Courier New", monospace;
          font-size: 10px;
          font-weight: 700;
          line-height: 1.3;
          white-space: nowrap;
        }
        .label-price {
          font-size: 11px;
          font-weight: 600;
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* On-screen only: center + frame the label so the popup preview is
           readable. Never affects print (overridden below). */
        @media screen {
          html, body, #root {
            background: #eef2f7;
            width: auto;
            height: auto;
            max-height: none;
            overflow: visible;
          }
          .print-label {
            margin: 24px auto;
            box-shadow: 0 8px 40px rgba(15, 23, 42, 0.25);
            border: 1px solid #cbd5e1;
          }
        }
        @media print {
          .print-label { margin: 0; box-shadow: none; border: none; }
        }
      `}</style>

      <div className="print-label">
        <div className="label-inner">
          <div className="qr-section">
            <QRCode className="qr" value={qrValue} size={256} />
          </div>
          <div className="text-section">
            <div className="label-title">{name}</div>
            <div className="label-code">{code}</div>
            {price !== '' && <div className="label-price">{price} DZD</div>}
          </div>
        </div>
      </div>
    </>
  );
}
